/**
 * Shared role-sort utilities for the GvG planner.
 *
 * Two related but distinct orderings live here:
 *
 *   1. `WEAPON_ROLE_ORDER` — the catalog order for individual weapons
 *      (TANK → DPS → HEAL). Weapon cards and the room's weapon chart use it.
 *   2. `PLAYER_ROLE_ORDER` — the roster order for derived player roles
 *      (TANK → DPS → HYBRID → HEAL). `sortIdsByRole` and
 *      `roleSortPriority` use this order.
 *
 * The picker uses a different (deliberately inverse) bucket order
 * (`WeaponPicker.ROLE_ORDER`: HEAL → TANK → DPS, "most-desired first"). That
 * asymmetry is intentional and documented there — the picker is for picking a
 * loadout, the board is for reading the roster.
 *
 * Three call sites previously re-implemented the same sort (the store,
 * `GvgPlannerView`, `PlayerGroup`); this module is the single source of truth
 * for the priority order and the stable sort algorithm. Keep new display-side
 * callers here rather than rolling their own ordering.
 */
import { PlayerRole, WeaponRole } from '@/types/weapon'

import type { Player, PlayerId } from '../types'

/**
 * Canonical display order for individual weapon roles. Lower index renders
 * earlier in a player's weapon cells and room weapon charts.
 */
export const WEAPON_ROLE_ORDER = [WeaponRole.Tank, WeaponRole.Dps, WeaponRole.Heal] as const

/** Canonical display order for persisted player roles in a roster. */
export const PLAYER_ROLE_ORDER = [
  PlayerRole.Tank,
  PlayerRole.Dps,
  PlayerRole.Hybrid,
  PlayerRole.Heal,
] as const

/**
 * @deprecated Use `PLAYER_ROLE_ORDER` instead — the old name implied this was
 * only for the "sort all" action, but it has become the canonical board display
 * order. Kept for the duration of one release to avoid breaking imports; new
 * code should reference `PLAYER_ROLE_ORDER` directly.
 */
export const ROLE_SORT_ORDER = PLAYER_ROLE_ORDER

/**
 * Numeric priority for a player role (0-based index in `PLAYER_ROLE_ORDER`).
 * Unknown roles are pushed past the last known entry so they always sort last.
 */
export function roleSortPriority(role: PlayerRole): number {
  const idx = PLAYER_ROLE_ORDER.indexOf(role)
  return idx === -1 ? PLAYER_ROLE_ORDER.length : idx
}

/**
 * Reorder player ids by display role priority (TANK → DPS → HYBRID → HEAL).
 * Within a role, original order is preserved — `Array.prototype.sort` is
 * stable in every supported engine. Ids whose player cannot be resolved are
 * pushed to the end of the list so the caller never silently drops them.
 */
export function sortIdsByRole(
  ids: ReadonlyArray<PlayerId>,
  players: ReadonlyArray<Player>,
): ReadonlyArray<PlayerId> {
  const roleById = new Map<PlayerId, PlayerRole>()
  for (const p of players) roleById.set(p.id, p.role)
  const decorated: Array<{ readonly id: PlayerId; readonly rank: number; readonly idx: number }> = []
  for (let i = 0; i < ids.length; i += 1) {
    const id = ids[i]
    if (id === undefined) continue
    const role = roleById.get(id)
    const rank = role === undefined ? PLAYER_ROLE_ORDER.length : roleSortPriority(role)
    decorated.push({ id, rank, idx: i })
  }
  decorated.sort((a, b) => (a.rank !== b.rank ? a.rank - b.rank : a.idx - b.idx))
  return decorated.map((d) => d.id)
}
